import { Injectable } from '@angular/core';
import * as chroma from 'chroma-js';
import { DatasourceColumnId } from '../utils/datasource-sql-map/datasource';

// Default color palette for ECharts
const DEFAULT_PALETTE = [
    '#E6194B',
    '#3CB44B',
    '#4363D8',
    '#F58231',
    '#911EB4',
    '#FFE119',
    '#42D4F4',
    '#800000',
    '#F032E6',
    '#BFEF45',
    '#000075',
    '#FABED4',
    '#469990',
    '#DCBEFF',
    '#9A6324',
    '#AAFFC3',
    '#808000',
    '#FFD8B1',
    '#A9A9A9',
    '#000000',
];
// '#FFFAC8',

/** Sign of the values a series carries — drives the semantic green / red colouring. */
export type ChartPolarity = 'positive' | 'negative';

@Injectable({
    providedIn: 'root',
})

/**
 * Service to manage chart colors.
 * It allows reserving specific colors for certain layers and keys,
 * and provides a mechanism to fetch and manage colors dynamically.
 */
export class ChartColorService {
    private store = new Map<string, Map<string, string>>();
    private reservation = new Map<string, Map<string, string>>();
    private availableColors = new Map<string, Array<string>>();

    constructor() {
        this.initializeDefaultReservations();
    }

    /**
     * Resets the color service.
     */
    reset() {
        this.store.clear();
        this.reservation.clear();
        this.availableColors.clear();
        this.initializeDefaultReservations();
    }

    /**
     * Reserves a color for a specific layer and key.
     * @param layer The layer for which the color is reserved.
     * @param key The key within the layer for which the color is reserved.
     * @param color The color to reserve.
     */
    private reserveColor(layer: string, key: string, color: string): void {
        if (!this.reservation.has(layer)) {
            this.reservation.set(layer, new Map<string, string>());
        }
        this.reservation.get(layer)!.set(key, color);
    }

    /**
     * Gets the color for a specified layer and key.
     * Returns a reserved color if available, otherwise allocates a new color.
     * @param layer The layer for which the color is requested.
     * @param key The key within the layer for which the color is requested.
     * @returns The color as a string.
     */
    getColor(layer: string, key: string): string {
        // if value in store, return value
        if (this.store.has(layer) && this.store.get(layer)!.has(key)) {
            return this.store.get(layer)!.get(key)!;
        }
        // prepare store
        if (!this.store.has(layer)) {
            this.store.set(layer, new Map<string, string>());
        }

        // if reservation exists, return the reserved color
        if (
            this.reservation.has(layer) &&
            this.reservation.get(layer)!.has(key)
        ) {
            const color = this.reservation.get(layer)!.get(key)!;
            // remove color from reservation
            this.reservation.get(layer)!.delete(key);

            // save color to store
            this.store.get(layer)!.set(key, color);
            return color;
        }

        // return next available color
        // if layer is not in availableColors, add layer to availableColors
        if (!this.availableColors.has(layer)) {
            this.initializePalette(layer);
        }
        // if layer is empty, reinitialize layer
        if (this.availableColors.get(layer)!.length === 0) {
            this.initializePalette(layer);
        }
        // get next color and remove from availableColors
        const color = this.availableColors.get(layer)!.shift()!;

        // save color to store
        this.store.get(layer)!.set(key, color);
        return color;
    }

    /**
     * Initializes the palette of available colors for a given layer.
     * Throws an error if the layer already has available colors.
     * @param layer The layer for which to initialize the palette.
     */
    private initializePalette(layer: string) {
        // throw error if availableColors is not empty
        if (
            this.availableColors.has(layer) &&
            this.availableColors.get(layer)!.length > 0
        ) {
            throw new Error(
                `initializePalette: availableColors for layer ${layer} is not empty`
            );
        }

        this.availableColors.set(
            layer,
            DEFAULT_PALETTE.filter((color) => {
                if (!this.reservation.has(layer)) {
                    return true;
                }
                return !this.reservation.get(layer)!.has(color);
            })
        );
    }

    /**
     * Initialize default color reservations.
     */
    private initializeDefaultReservations() {
        this.reserveColor(
            ChartColorService.getLayerString(['COMPANY_PROJECT_NAME']),
            'Bladebound',
            DEFAULT_PALETTE[0]
        );
        this.reserveColor(
            ChartColorService.getLayerString(['COMPANY_PROJECT_NAME']),
            'MOPA',
            DEFAULT_PALETTE[1]
        );
    }

    /**
     * Returns the default color used by ECharts.
     * @returns The default color as a string.
     */
    static getDefaultColor() {
        return '#5470c6'; // echarts default color
        return DEFAULT_PALETTE[0];
    }

    /**
     * Semantic gain/loss colours — green for positive, red for negative.
     * Shared by the net-balance chart (≥ 0 / < 0) and by single-series positive/negative charts so
     * the whole family reads coherently. Single source of truth for the pair.
     */
    static readonly POSITIVE_COLOR = '#43a047';
    static readonly NEGATIVE_COLOR = '#e53935';

    // Per-polarity ramps, built directly in HCL. Hue sweeps linearly across the family; lightness
    // follows three control points [start, mid, end], piecewise-linear (start→mid over the first
    // half, mid→end over the second), so each side runs dark → light → dark and every hue spans its
    // full light range. `zigzag` then adds an alternating ± light offset so neighbours jump light/
    // dark even when many series squeeze the ramp (0 = pure gradient). Widen `hue`, spread `light`,
    // raise `sat`, or bump `zigzag` to push contrast.
    private static readonly POLARITY_RAMP = {
        positive: { hue: [120, 320], light: [50, 90, 50], sat: 75, offset: 14 },
        negative: { hue: [20, 100], light: [50, 90, 50], sat: 75, offset: 14 },
    } as const;

    /**
     * Colours for the `count` series of one polarity on a two-sided chart, as an ordered family
     * gradient (positive ▲ greens → cyan / negative ▼ reds → yellow) with an alternating light/dark
     * zig-zag on top so neighbours stay distinct. The largest series (index 0) gets the dark starting
     * end. A single series returns the mid semantic green / red. Colours are positional, not keyed —
     * they re-space when the set size changes.
     */
    static getPolarityShades(polarity: ChartPolarity, count: number): string[] {
        if (count <= 0) {
            return [];
        }
        if (count === 1) {
            return [
                polarity === 'positive'
                    ? this.POSITIVE_COLOR
                    : this.NEGATIVE_COLOR,
            ];
        }
        const { hue, light, sat, offset } = this.POLARITY_RAMP[polarity];
        return Array.from({ length: count }, (_, i) => {
            const t = i / (count - 1);
            const h = hue[0] + (hue[1] - hue[0]) * t;
            // piecewise lightness across the three control points (peak in the middle).
            const base =
                t < 0.5
                    ? light[0] + (light[1] - light[0]) * (t / 0.5)
                    : light[1] + (light[2] - light[1]) * ((t - 0.5) / 0.5);
            // alternating ± offset so consecutive series jump light/dark (the plain step span/(N-1)
            // shrinks as count grows); clamp off near-black / near-white.
            const l = Math.max(
                12,
                Math.min(
                    93,
                    base +
                        (i % 2 === 0
                            ? -offset
                            : i % 3 === 0
                              ? -offset * 2
                              : offset)
                )
            );
            return chroma.hcl(h, sat, l).hex();
        });
    }

    /**
     * Converts an array of DatasourceColumnId into a single string representing a layer.
     * This is useful for creating unique layer identifiers.
     * @param arr An array of strings representing different parts of a layer.
     * @returns A string representing the layer.
     */
    static getLayerString(arr: Array<DatasourceColumnId>) {
        return arr.join('::');
    }
}
